import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, useTheme, useMediaQuery } from '@ublend-npm/aulaui-core';
import push from '../../../utils/routing';
import { useAllSpaces } from '../../../hooks/useAllSpaces';
import SpacesList from './SpacesList';
import WidgetCard from '../Home/widgets/common/WidgetCard';
import NavigationBackHeader from '../common/NavigationBackHeader/NavigationBackHeader';

interface AllSpacesPageProps {
  onClose?: () => void;
  filterType?: 'all' | 'starred' | 'recent' | 'all_from_starred';
}

const getQueryParam = (key: string) => {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has(key)) return searchParams.get(key);

  const hash = window.location.hash;
  if (hash.includes('?')) {
    const hashParams = new URLSearchParams(hash.substring(hash.indexOf('?')));
    return hashParams.get(key);
  }
  return null;
};

const AllSpacesPage: React.FC<AllSpacesPageProps> = ({
  onClose,
  filterType: propFilterType,
}) => {
  const dispatch = useDispatch();
  const urlFilter = getQueryParam('filter') as
    | 'all'
    | 'starred'
    | 'recent'
    | 'all_from_starred';
  const filterType = propFilterType || urlFilter || 'all';

  const allSpacesData = useAllSpaces(filterType);

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      push({ pathname: '/home', state: { tracked: true } });
    }
  };

  // Ensure global loader is cleared when this page mounts or unmounts
  useEffect(() => {
    dispatch({
      type: 'SPACE_SELECT_LOADING',
      payload: { isSpaceLoading: false },
    });
    return () => {
      dispatch({
        type: 'SPACE_SELECT_LOADING',
        payload: { isSpaceLoading: false },
      });
    };
  }, [dispatch]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Auto-redirect if it's the starred view and the user unstars the last space
  useEffect(() => {
    if (
      filterType === 'starred' &&
      allSpacesData.spaces.length === 0 &&
      !allSpacesData.isLoading
    ) {
      handleBack();
    }
  }, [filterType, allSpacesData.spaces.length, allSpacesData.isLoading]);

  // Redirect to home modal view if resizing from mobile to desktop while on this standalone route
  useEffect(() => {
    // Only redirect to home modal view when on the exact '/all-spaces' route, not on mobile, and not a modal.
    const isAllSpacesRoute =
      window.location.pathname.includes('/all-spaces') ||
      window.location.hash.includes('/all-spaces');
    if (!isMobile && !onClose && isAllSpacesRoute) {
      push(`/home?openModal=${filterType}`);
    }
  }, [isMobile, onClose, filterType]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', md: '800px' },
        margin: '0 auto',
        p: { xs: 2, md: 4 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <NavigationBackHeader onClick={handleBack} wrapperStyle={{ mb: 3 }} />

      <Box
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <WidgetCard
          title={
            filterType === 'starred'
              ? 'Starred spaces'
              : filterType === 'recent'
              ? 'Recently viewed spaces'
              : 'All spaces'
          }
          minHeight="100%"
          borderRadius="8px"
        >
          <Box sx={{ overflowY: 'auto', flex: 1, height: '100%', pr: 1 }}>
            <SpacesList
              {...allSpacesData}
              hideEmptyState={filterType === 'starred'}
              onClose={onClose}
              listType={filterType}
            />
          </Box>
        </WidgetCard>
      </Box>
    </Box>
  );
};

export default AllSpacesPage;
